import { isBookingProcessAlias } from '../transactions/transaction';

/**
 * SelectMultipleFilter needs to parse values from format
 * "has_all:a,b,c,d" or "a,b,c,d"
 */
export const parseSelectFilterOptions = uriComponentValue => {
  const startsWithHasAll = uriComponentValue && uriComponentValue.indexOf('has_all:') === 0;
  const startsWithHasAny = uriComponentValue && uriComponentValue.indexOf('has_any:') === 0;

  if (startsWithHasAll) {
    return uriComponentValue.substring(8).split(',');
  } else if (startsWithHasAny) {
    return uriComponentValue.substring(8).split(',');
  } else {
    return uriComponentValue.split(',');
  }
};

/**
 * Create the name of the query parameter.
 *
 * @param {String} key Key extracted from listingExtendData config.
 * @param {String} scope Scope extracted from listingExtendData config.
 */
export const constructQueryParamName = (key, scope) => {
  const prefixedKey = scope === 'meta' ? `meta_${key}` : `pub_${key}`;
  return prefixedKey.replace(/\s/g, '_');
};

const SEARCH_MODES = ['has_all', 'has_any'];

/**
 * Search string filtering listings to a single option of a multi-enum field,
 * e.g. '?pub_Region=has_all:Zuerich'.
 *
 * Returns null when the field is not indexed for search: the API cannot query
 * such a param, so a link would silently land on unfiltered results.
 *
 * @param {String} key Listing field key, e.g. 'Region'.
 * @param {String} optionKey A single option of that field, e.g. 'Zuerich'.
 * @param {Object} config The field's config: { scope, filterConfig }.
 */
export const listingFieldSearchParam = (key, optionKey, config = {}) => {
  const { scope, filterConfig } = config;
  if (!filterConfig?.indexForSearch) {
    return null;
  }
  const searchMode = SEARCH_MODES.includes(filterConfig.searchMode)
    ? filterConfig.searchMode
    : 'has_all';
  const paramName = constructQueryParamName(key, scope);
  return `?${paramName}=${searchMode}:${encodeURIComponent(optionKey)}`;
};

/**
 * Get parameter names for search query. Extract those from config.
 * The configuration of default filters has key, which is 1-on-1 mapping
 * with the name of the query parameter. E.g. 'price'.
 *
 * @param {Object} listingFieldsConfig Custom filters are checked agains extended data config of a listing entity.
 * @param {Object} defaultFiltersConfig Configuration of default filters.
 */
export const getQueryParamNames = (listingFieldsConfig, defaultFiltersConfig) => {
  const queryParamKeysOfDefaultFilters = defaultFiltersConfig.reduce((pickedKeys, config) => {
    const { key, schemaType, scope, nestedParams } = config;
    const newKeys =
      schemaType === 'category' && nestedParams
        ? nestedParams?.map(p => constructQueryParamName(p, scope))
        : schemaType === 'listingType'
        ? [constructQueryParamName(key, scope)]
        : [key];
    return [...pickedKeys, ...newKeys];
  }, []);
  const queryParamKeysOfListingFields = listingFieldsConfig.reduce((params, config) => {
    const param = constructQueryParamName(config.key, config.scope);
    return config.filterConfig?.indexForSearch ? [...params, param] : params;
  }, []);
  return [...queryParamKeysOfDefaultFilters, ...queryParamKeysOfListingFields];
};
/**
 * Check if any of the filters (defined by filterKeys) have currently active query parameter in URL.
 */
export const isAnyFilterActive = (filterKeys, urlQueryParams, filterConfigs) => {
  const { listingFieldsConfig, defaultFiltersConfig } = filterConfigs;
  const queryParamKeys = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

  const getQueryParamKeysOfGivenFilters = (pickedKeys, key) => {
    const isFilterIncluded = filterKeys.includes(key);
    const addedQueryParamNamesMaybe = isFilterIncluded ? [key] : [];
    return [...pickedKeys, ...addedQueryParamNamesMaybe];
  };
  const queryParamKeysOfGivenFilters = queryParamKeys.reduce(getQueryParamKeysOfGivenFilters, []);

  const paramEntries = Object.entries(urlQueryParams);
  const activeKey = paramEntries.find(entry => {
    const [key, value] = entry;
    return queryParamKeysOfGivenFilters.includes(key) && value != null;
  });
  return !!activeKey;
};

/**
 * Pick initial vales for FieldSelectTree component.
 * The value object should be an object containing search params:
 * { pub_categoryLevel1: 'cats', pub_categoryLevel2: 'egyptian-mau', meta_foo: 'bar' }
 *
 * @param {String} prefix like "pub_categoryLevel"
 * @param {Object} values object literal containing level-specific info
 *
 * @returns returns properties, which have a key that starts with the given prefix.
 */
export const pickInitialValuesForFieldSelectTree = (prefix, values, isNestedEnum) => {
  const pickValuesFn = (picked, entry) => {
    const [key, value] = entry;
    const prefixIndex = key.indexOf(prefix);
    const startsWithPrefix = prefixIndex > -1;
    const slicedKey = isNestedEnum ? key.slice(prefixIndex) : `${key.slice(prefixIndex)}1`;
    return startsWithPrefix ? { ...picked, [slicedKey]: value } : picked;
  };
  const prefixCollection = Object.entries(values).reduce(pickValuesFn, {});
  return prefixCollection;
};

/**
 * This converts the category structure to the format that that's understood by SelectSingleFilter
 * and its child component: FieldSelectTree.
 *
 * @param {Array} categories contain objects with props: _id_, _name_, potentially _subcategories_.
 * @returns an array that contains objects with props: _option_, _label_ and potentially _suboptions_.
 */
export const convertCategoriesToSelectTreeOptions = categories => {
  const convertSubcategoryData = params => {
    const { id, name, subcategories } = params;
    const suboptionsMaybe = subcategories
      ? { suboptions: subcategories.map(cat => convertSubcategoryData(cat)) }
      : {};
    return { option: id, label: name, ...suboptionsMaybe };
  };

  const categoriesArray = Array.isArray(categories) ? categories : [];
  return categoriesArray.map(cat => convertSubcategoryData(cat));
};

/**
 * Check if the main search type is 'keywords'
 */
export const isMainSearchTypeKeywords = config =>
  config.search?.mainSearch?.searchType === 'keywords';

/**
 * Check if the origin parameter is currently active.
 */
export const isOriginInUse = config =>
  config.search?.mainSearch?.searchType === 'location' && config.maps?.search?.sortSearchByDistance;

/**
 * Check if the stock management is currently active.
 */
export const isStockInUse = config => {
  const listingTypes = config.listing.listingTypes;
  const stockProcesses = ['default-purchase'];
  const hasStockProcessesInUse = !!listingTypes.find(conf =>
    stockProcesses.includes(conf.transactionType.process)
  );

  // Note: these are active processes!
  return hasStockProcessesInUse;
};

/**
 * How one listing stands relative to the date the buyer searched for.
 *
 * Three-way on purpose, not `set.has(id) ? available : unavailable`:
 *
 * - `null` set means unresolved — no date active, probe in flight, or probe failed. An
 *   unresolved set licenses no claim about anyone; a failed request must never hide every
 *   supplier.
 * - Only a booking-process listing with an `availabilityPlan` can be "Belegt" on the 14th of
 *   June. A Produktkauf item is not date-constrained, so a dated search must never sweep it off
 *   the page, even though the listing editor gives it an empty plan.
 *
 * Reading this correctly depends on the search query having asked for `availabilityPlan` — see
 * the field list in SearchPage.duck.js, where omitting it makes every listing look plan-less
 * and this rule silently hide nobody.
 *
 * @param {Object} listing
 * @param {Set<string>|null} availableIds resolved ids, or null while unresolved
 * @returns {'available'|'unavailable'|null}
 */
const availabilityForListing = (listing, availableIds) => {
  if (!availableIds) {
    return null;
  }
  if (availableIds.has(listing?.id?.uuid)) {
    return 'available';
  }
  const { availabilityPlan, publicData } = listing?.attributes || {};
  return availabilityPlan && isBookingProcessAlias(publicData?.transactionProcessAlias)
    ? 'unavailable'
    : null;
};

/**
 * The listings a dated search actually shows.
 *
 * A visitor who searched a date is shown the suppliers who can serve it, and nothing else —
 * there is no control that reveals the booked ones, so the hiding is unconditional.
 *
 * It lives here rather than in SearchResultsPanel because the list is not the only thing that
 * renders these results: the map variant plots pins from the same set, and a pin for a supplier
 * the list has hidden is worse than either extreme — the visitor can click it and open the info
 * card of someone the page decided not to show them. One rule, one place, so the two cannot drift.
 *
 * Availability is resolved for the ids on ONE page only, so this is page-scoped and no caller
 * may claim otherwise.
 *
 * @param {Array<Object>} listings
 * @param {Array<string>|null} availableListingIds ids free on the searched date, or null while
 *   unresolved (no date, probe in flight, or probe failed)
 * @returns {Array<Object>} the listings to render, in the order they were given
 */
export const visibleListingsForDate = (listings = [], availableListingIds = null) => {
  // Kept null when unresolved so the distinction survives into availabilityForListing: an empty
  // Set would mean "nobody is free", which is a different and much louder claim.
  const availableIds = Array.isArray(availableListingIds) ? new Set(availableListingIds) : null;
  return listings.filter(
    listing => availabilityForListing(listing, availableIds) !== 'unavailable'
  );
};

/**
 * Check whether the topbar's keyword segment ("Was") is switched on.
 *
 * Off by default, so an environment that says nothing gets the Wann/Wo pill alone. It is a
 * separate switch from mainSearch.searchType: that setting decides WHICH free-text control the
 * topbar would use, not whether the topbar offers one at all, and turning it to 'location'
 * would swap in the Mapbox autocomplete rather than remove the field.
 *
 * Nothing else changes when it is off. `keywords` is still read from the URL and still
 * submitted when present, so a /s?keywords= link keeps working and turning the segment back on
 * restores the control over a filter that never went away.
 */
export const isTopbarKeywordSearchEnabled = () =>
  process.env.REACT_APP_TOPBAR_KEYWORD_SEARCH === 'true';

/**
 * Check whether the flexible-date search is switched on.
 *
 * The gate lives on the `dates` default filter and is set from
 * REACT_APP_DATE_SEARCH_ENABLED in configHelpers' mergeSearchConfig — the hosted
 * listing-search asset replaces that filter wholesale, so the env var is the
 * authority, not the value in configSearch.js.
 */
export const isDateSearchEnabled = config =>
  !!config.search?.defaultFilters?.find(f => f?.schemaType === 'dates')?.dateSearch;
